// Canonical specialty table: the `:specialty` URL slug ↔ NUCC taxonomy code
// mapping, plus the display strings the UI and the mock generator use.
//
// This is production code (the search form and the results loader both consume
// it), so it deliberately lives outside `app/mocks/`. The mock generator
// round-robins across this same list, which is what makes a search for a slug
// return providers whose specialty actually matches.
//
// Every `nuccCode` must exist in `app/mocks/taxonomy-search.ts` SPECIALTY_SEEDS
// — `specialties.unit.test.ts` asserts that, so the two can't drift.
//
// Slugs are unique; NUCC codes deliberately are not. "Primary Care Provider"
// and "Family Doctor" are distinct search intents the picker offers separately,
// but they resolve to the same taxonomy code (207Q00000X) and therefore the same
// roster. `canonicalSlug` marks the duplicate so the results page can point
// search engines at one URL, and `UNIQUE_NUCC_SPECIALTIES` below is what
// anything reasoning about searches rather than picker options should use.

export type Specialty = {
  // The `:specialty` path segment, e.g. /us/portland--or/cardiologist.
  slug: string
  // The NUCC taxonomy code submitted to the providers API.
  nuccCode: string
  // Text shown in the search form's specialty <select>, and in the results
  // heading when exactly one provider matched.
  label: string
  // Plural of `label`, for anywhere the specialty names a group: the results
  // heading and <title>, the Browse-by-city links, and the search form's
  // specialty picker (VIT-17926). Explicit rather than derived: every current
  // row happens to take a plain "s", but the labels are free English
  // ("Pediatric Medicine" was a row until recently, and "Pediatric Medicines"
  // is not a word), so a naive rule would break the next irregular one silently.
  pluralLabel: string
  // How the specialty is named on a provider record (Recommendation.specialty).
  displayName: string
  // Set only on a row that shares its NUCC code with another row: the slug of
  // the row search engines should treat as the original. Both URLs return the
  // same providers, so the results page emits <link rel=canonical> to that one.
  canonicalSlug?: string
}

export const SPECIALTIES: Specialty[] = [
  {
    slug: 'primary-care-provider',
    nuccCode: '207Q00000X',
    label: 'Primary Care Provider',
    pluralLabel: 'Primary Care Providers',
    displayName: 'Family Medicine',
  },
  {
    slug: 'family-doctor',
    nuccCode: '207Q00000X',
    label: 'Family Doctor',
    pluralLabel: 'Family Doctors',
    displayName: 'Family Medicine',
    canonicalSlug: 'primary-care-provider',
  },
  {
    slug: 'obgyn',
    nuccCode: '207V00000X',
    label: 'OB-GYN',
    pluralLabel: 'OB-GYNs',
    displayName: 'Obstetrics & Gynecology',
  },
  {
    slug: 'dermatologist',
    nuccCode: '207N00000X',
    label: 'Dermatologist',
    pluralLabel: 'Dermatologists',
    displayName: 'Dermatology',
  },
  { slug: 'dentist', nuccCode: '122300000X', label: 'Dentist', pluralLabel: 'Dentists', displayName: 'Dentist' },
  {
    slug: 'ent',
    nuccCode: '207Y00000X',
    label: 'ENT Specialist',
    pluralLabel: 'ENT Specialists',
    displayName: 'Otolaryngology',
  },
  {
    slug: 'ophthalmologist',
    nuccCode: '207W00000X',
    label: 'Ophthalmologist',
    pluralLabel: 'Ophthalmologists',
    displayName: 'Ophthalmology',
  },
  {
    slug: 'psychiatrist',
    nuccCode: '2084P0800X',
    label: 'Psychiatrist',
    pluralLabel: 'Psychiatrists',
    displayName: 'Psychiatry',
  },
  {
    slug: 'orthopedic-surgeon',
    nuccCode: '207X00000X',
    label: 'Orthopedic Surgeon',
    pluralLabel: 'Orthopedic Surgeons',
    displayName: 'Orthopedic Surgery',
  },
  {
    slug: 'cardiologist',
    nuccCode: '207RC0000X',
    label: 'Cardiologist',
    pluralLabel: 'Cardiologists',
    displayName: 'Cardiology',
  },
  {
    slug: 'gastroenterologist',
    nuccCode: '207RG0100X',
    label: 'Gastroenterologist',
    pluralLabel: 'Gastroenterologists',
    displayName: 'Gastroenterology',
  },
  { slug: 'urologist', nuccCode: '208800000X', label: 'Urologist', pluralLabel: 'Urologists', displayName: 'Urology' },
  {
    slug: 'neurologist',
    nuccCode: '2084N0400X',
    label: 'Neurologist',
    pluralLabel: 'Neurologists',
    displayName: 'Neurology',
  },
]

// The slug the search form pre-selects when the URL carries no `:specialty`.
// It used to fall out of SPECIALTIES being declared with this row first, but the
// picker now renders SPECIALTIES_BY_LABEL, so the default has to be explicit —
// otherwise it would silently become whatever sorts first ("Cardiologist").
export const DEFAULT_SPECIALTY_SLUG = 'primary-care-provider'

// SPECIALTIES ordered by display label, for anything the user reads: the search
// form's <select> and the Browse-by-city specialty lists (VIT-17723). Derived
// rather than applied to SPECIALTIES itself, because the table's declaration
// order is load-bearing — UNIQUE_NUCC_SPECIALTIES keeps the *first* row of each
// duplicated NUCC code, and alphabetically the duplicate ("Family Doctor")
// precedes the canonical row ("Primary Care Provider").
export const SPECIALTIES_BY_LABEL: Specialty[] = [...SPECIALTIES].sort((a, b) => a.label.localeCompare(b.label))

// SPECIALTIES with duplicate NUCC codes collapsed to their first row. Anything
// that reasons about *searches* rather than *picker options* wants this: two
// picker rows share 207Q00000X, and a search for either returns one roster.
export const UNIQUE_NUCC_SPECIALTIES: Specialty[] = SPECIALTIES.filter(
  (specialty, index) => SPECIALTIES.findIndex((other) => other.nuccCode === specialty.nuccCode) === index,
)

/** Resolve a `:specialty` path segment to its entry; undefined for unknown slugs. */
export const specialtyForSlug = (slug: string): Specialty | undefined =>
  SPECIALTIES.find((specialty) => specialty.slug === slug)

/**
 * Reverse lookup: the specialty a provider record NAMES (`displayName`) back to
 * its registry row. Undefined when no row carries that name.
 *
 * The one caller is the provider page's breadcrumb trail (VIT-17746), which needs
 * a results URL for the middle rung and only has the provider's own specialty
 * string to build it from. Undefined is a normal outcome, not a fault: the
 * provider endpoint resolves `specialty` from NPPES/Ribbon across the whole NUCC
 * taxonomy, while this registry is a curated dozen — so most providers' specialty
 * has no results page to point at, and the trail falls back to two rungs.
 *
 * UNIQUE_NUCC_SPECIALTIES, not SPECIALTIES: "Family Medicine" is the displayName
 * of both `primary-care-provider` and `family-doctor`, and results.tsx already
 * points the latter's canonical at the former — so a breadcrumb must land on the
 * canonical row rather than on the URL that redirects search engines away.
 *
 * Case- and whitespace-insensitive, because the value is upstream text.
 */
export const specialtyForDisplayName = (displayName: string): Specialty | undefined => {
  const needle = displayName.trim().toLowerCase()
  if (needle === '') return undefined
  return UNIQUE_NUCC_SPECIALTIES.find((specialty) => specialty.displayName.toLowerCase() === needle)
}
